import React, { useState } from 'react';
import styled from 'styled-components';
import backgroundVideo from '../assets/video.mp4';
import videoPlaceholder from '../assets/video-placeholder.png';

const HeroSection = () => {
  const [isVideoLoaded, setIsVideoLoaded] = useState(false); // Para saber quando o vídeo está carregado

  const scrollToSection = () => {
    const section = document.getElementById('about-section');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Função que será chamada quando o vídeo for completamente carregado
  const handleVideoLoad = () => {
    setIsVideoLoaded(true);
  };

  return (
    <Section>
      <PosterBackground $isVideoLoaded={isVideoLoaded} />
      <VideoBackground
        autoPlay
        loop
        muted
        playsInline
        preload="metadata"
        poster={videoPlaceholder}
        onLoadedData={handleVideoLoad}
      >
        <source src={backgroundVideo} type="video/mp4" />
      </VideoBackground>
      <Overlay />
      <HeroContainer>
        <HeroCopy>
          <Eyebrow>Engenharia solar completa</Eyebrow>
          <HeroTitle>Energia limpa com performance e economia real</HeroTitle>
          <HeroText>
            Projetamos e instalamos sistemas fotovoltaicos que reduzem custos, valorizam o imóvel e garantem
            previsibilidade energética para sua empresa ou residência.
          </HeroText>
          <CTAGroup>
            <PrimaryButton onClick={scrollToSection}>Conheça as soluções</PrimaryButton>
            <GhostButton href="https://www.instagram.com/gfsolucoesengenharia/" target="_blank" rel="noreferrer">
              Ver projetos no Instagram
            </GhostButton>
          </CTAGroup>
          <StatsRow>
            <StatCard>
              <StatValue>+500</StatValue>
              <StatLabel>Projetos entregues</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>24/7</StatValue>
              <StatLabel>Monitoramento</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>100%</StatValue>
              <StatLabel>Engenharia própria</StatLabel>
            </StatCard>
          </StatsRow>
        </HeroCopy>
        <HeroPanel>
          <PanelHeader>
            <PanelTitle>Por que GF Soluções?</PanelTitle>
            <PanelSubtitle>Eficiência técnica com atenção total ao cliente.</PanelSubtitle>
          </PanelHeader>
          <PanelList>
            <PanelItem>
              <PanelItemTitle>Estudo de viabilidade completo</PanelItemTitle>
              <PanelItemText>Dimensionamento sob medida e simulações detalhadas.</PanelItemText>
            </PanelItem>
            <PanelItem>
              <PanelItemTitle>Equipe especializada</PanelItemTitle>
              <PanelItemText>Engenharia dedicada do projeto à homologação.</PanelItemText>
            </PanelItem>
            <PanelItem>
              <PanelItemTitle>Suporte próximo</PanelItemTitle>
              <PanelItemText>Relacionamento transparente e acompanhamento contínuo.</PanelItemText>
            </PanelItem>
          </PanelList>
          <PanelFooter>
            Pronto para começar? Fale com nossos especialistas e receba um diagnóstico gratuito.
          </PanelFooter>
        </HeroPanel>
      </HeroContainer>
    </Section>
  );
};

export default HeroSection;

// Estilos com styled-components

const Section = styled.section`
  position: relative;
  height: 100vh;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  padding: 400px 24px;

  @media (min-width: 900px) {
    padding: 0 8%;
  }
`;

const PosterBackground = styled.div`
  position: absolute;
  inset: 0;
  background-image: url(${videoPlaceholder});
  background-size: cover;
  background-position: center;
  opacity: ${({ $isVideoLoaded }) => ($isVideoLoaded ? 0 : 1)};
  transition: opacity 0.6s ease;
  z-index: -1;
`;

const VideoBackground = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover; /* O vídeo vai cobrir toda a tela, mantendo a proporção */
  z-index: -1; /* Coloca o vídeo atrás do conteúdo */
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(11, 15, 26, 0.92), rgba(11, 15, 26, 0.6));
  z-index: 0;
`;

const HeroContainer = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 40px;
  align-items: center;
  width: 100%;
  max-width: 1200px;
`;

const HeroCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  color: ${({ theme }) => theme.colors.white};
`;

const Eyebrow = styled.span`
  display: inline-flex;
  width: fit-content;
  padding: 6px 16px;
  border-radius: 999px;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  background: rgba(255, 199, 0, 0.18);
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;
`;

const HeroTitle = styled.h1`
  font-size: clamp(2.6rem, 4vw, 4rem);
  color: ${({ theme }) => theme.colors.primary};
`;

const HeroText = styled.p`
  font-size: 1.2rem;
  margin: 0;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.6;
`;

const CTAGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

const PrimaryButton = styled.button`
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.text};
  padding: 14px 28px;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  border-radius: 10px;
  font-weight: 600;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 30px rgba(255, 199, 0, 0.3);
  }
`;

const GhostButton = styled.a`
  padding: 14px 24px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  color: #fff;
  text-decoration: none;
  font-weight: 500;
  transition: background 0.3s ease, border 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.7);
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 16px;
`;

const StatCard = styled.div`
  padding: 16px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
`;

const StatLabel = styled.div`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 4px;
`;

const HeroPanel = styled.aside`
  background: rgba(11, 15, 26, 0.7);
  border-radius: 24px;
  padding: 28px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 24px 60px rgba(5, 9, 21, 0.6);
  color: #fff;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const PanelHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PanelTitle = styled.h3`
  margin: 0;
  font-size: 1.4rem;
  color: ${({ theme }) => theme.colors.primary};
`;

const PanelSubtitle = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.7);
`;

const PanelList = styled.div`
  display: grid;
  gap: 16px;
`;

const PanelItem = styled.div`
  padding: 16px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const PanelItemTitle = styled.h4`
  margin: 0 0 6px;
  font-size: 1rem;
  color: #fff;
`;

const PanelItemText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
`;

const PanelFooter = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
`;
